import { useCallback, useMemo, useState } from 'react';
import {
  isLockedByOtherUser,
  isLockedBySystem,
  LoadedDocument,
} from '../../../model/LoadedDocument';
import { useCurrentUser } from '../../../contexts/UserContext';
import { useLoadedDocument } from '../../v2-hooks/useLoadedDocument';

export interface CommentCreationPopoverViewModelProps {
  readonly textHint: string;
  readonly onSubmit: (body: string) => Promise<void>;
  readonly onDismiss: () => void;
}

export interface CommentCreationPopoverViewModel {
  readonly textHint: string;
  readonly commentBody: string;
  readonly setCommentBody: (value: string) => void;
  readonly isSubmitting: boolean;
  readonly error: string | undefined;
  readonly dismissError: () => void;
  readonly handleSubmit: () => void;
  readonly handleDismiss: () => void;
  readonly isSubmitDisabled: boolean;
  readonly disabledReason: string | undefined;
  readonly computedRows: number;
}

export function computeCommentDisabledReason(
  doc: LoadedDocument | undefined,
  currentUserId: string,
): string | undefined {
  if (!doc || doc.kind === 'publication') return undefined;

  if (isLockedByOtherUser(doc.workflow, currentUserId)) {
    return 'Adding comment is not allowed, the content is being edited by someone else';
  }
  if (isLockedBySystem(doc.workflow)) {
    return 'Adding comment is not allowed while the content is being processed';
  }
  if (!doc.workflow.isLocked) return undefined;

  // Self-lock: current user holds the lock
  return 'Adding comment is not allowed while you are editing the content';
}

const MIN_ROWS = 3;
const MAX_ROWS = 12;

export function useCommentCreationPopoverViewModel(
  props: CommentCreationPopoverViewModelProps,
): CommentCreationPopoverViewModel {
  const { textHint, onSubmit, onDismiss } = props;
  const doc = useLoadedDocument();
  const currentUser = useCurrentUser();

  const [commentBody, setCommentBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const disabledReason = useMemo(
    () => computeCommentDisabledReason(doc, currentUser.userId),
    [doc, currentUser.userId],
  );

  const isSubmitDisabled =
    disabledReason !== undefined || commentBody.trim().length === 0 || isSubmitting;

  const computedRows = useMemo(() => {
    const lineCount = commentBody.split('\n').length;
    return Math.max(MIN_ROWS, Math.min(lineCount + 1, MAX_ROWS));
  }, [commentBody]);

  const dismissError = useCallback(() => {
    setError(undefined);
  }, []);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const handleSubmit = useCallback(() => {
    const trimmed = commentBody.trim();
    if (trimmed.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    setError(undefined);

    onSubmit(trimmed)
      .then(() => {
        onDismiss();
      })
      .catch(() => {
        setError('There was an error trying to add comment, please try again later');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [commentBody, isSubmitting, onSubmit, onDismiss]);

  return {
    textHint,
    commentBody,
    setCommentBody,
    isSubmitting,
    error,
    dismissError,
    handleSubmit,
    handleDismiss,
    isSubmitDisabled,
    disabledReason,
    computedRows,
  };
}
